import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Phone, Mail, Calendar as CalendarIcon, StickyNote, CheckSquare,
  TrendingUp, Target, Building2, Users, Trash2, Edit, UserPlus, X,
  MessageSquare, Briefcase, DollarSign, Search, Download, Upload, ChevronDown,
  FileSpreadsheet, FileJson, CheckCircle2, AlertCircle, Database } from 'lucide-react';
import SearchableSelect from '@/components/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { useCurrency } from '@/contexts/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'qualified', label: 'Qualified', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'proposal', label: 'Proposal', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'won', label: 'Won', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'lost', label: 'Lost', color: 'bg-rose-100 text-rose-700 border-rose-200' },
];

const ACTIVITY_ICONS = {
  call: Phone, email: Mail, meeting: CalendarIcon, note: StickyNote, task: CheckSquare
};

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-rose-100 text-rose-700',
};

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function CRM() {
  const [tab, setTab] = useState('pipeline');
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  const loadAll = async () => {
    try {
      const [leadsRes, compRes, statsRes] = await Promise.all([
        axios.get(`${API}/crm/leads`, { headers: getHeaders() }),
        axios.get(`${API}/crm/companies`, { headers: getHeaders() }),
        axios.get(`${API}/crm/stats`, { headers: getHeaders() }),
      ]);
      setLeads(leadsRes.data);
      setCompanies(compRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openNewLead = () => { setEditingLead({}); setShowLeadDialog(true); };
  const openEditLead = (lead) => { setEditingLead(lead); setShowLeadDialog(true); };
  const openNewCompany = () => { setEditingCompany({}); setShowCompanyDialog(true); };
  const openEditCompany = (c) => { setEditingCompany(c); setShowCompanyDialog(true); };

  const saveLead = async (form) => {
    try {
      if (editingLead?.id) {
        await axios.put(`${API}/crm/leads/${editingLead.id}`, form, { headers: getHeaders() });
        toast.success('Lead updated');
      } else {
        await axios.post(`${API}/crm/leads`, form, { headers: getHeaders() });
        toast.success('Lead created');
      }
      setShowLeadDialog(false); setEditingLead(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save lead');
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This removes all linked activities.`)) return;
    try {
      await axios.delete(`${API}/crm/leads/${lead.id}`, { headers: getHeaders() });
      toast.success('Lead deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  const convertLead = async (lead) => {
    if (!window.confirm(`Convert "${lead.name}" to a client? This will also mark the lead as Won.`)) return;
    try {
      const res = await axios.post(`${API}/crm/leads/${lead.id}/convert`, {}, { headers: getHeaders() });
      toast.success(`Converted to client: ${res.data.client.name}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };

  const saveCompany = async (form) => {
    try {
      if (editingCompany?.id) {
        await axios.put(`${API}/crm/companies/${editingCompany.id}`, form, { headers: getHeaders() });
        toast.success('Company updated');
      } else {
        await axios.post(`${API}/crm/companies`, form, { headers: getHeaders() });
        toast.success('Company created');
      }
      setShowCompanyDialog(false); setEditingCompany(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/crm/companies/${c.id}`, { headers: getHeaders() });
      toast.success('Company deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="p-8 text-slate-500" data-testid="crm-loading">Loading CRM…</div>;

  const filteredLeads = (() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.phone || '').toLowerCase().includes(q) ||
      (l.company_name || '').toLowerCase().includes(q) ||
      (l.title || '').toLowerCase().includes(q) ||
      (l.status || '').toLowerCase().includes(q) ||
      (l.source || '').toLowerCase().includes(q) ||
      (l.notes || '').toLowerCase().includes(q)
    );
  })();
  const filteredCompanies = (() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.industry || '').toLowerCase().includes(q) ||
      (c.website || '').toLowerCase().includes(q) ||
      (c.notes || '').toLowerCase().includes(q)
    );
  })();

  const exportLeadsCSV = () => {
    if (!filteredLeads.length) { toast.error('Nothing to export'); return; }
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Name','Email','Phone','Company','Title','Status','Priority','Value','Currency','Source','Notes','Created'];
    const lines = [headers.join(','), ...filteredLeads.map(l => [
      l.name, l.email, l.phone, l.company_name, l.title, l.status, l.priority,
      l.value, l.currency, l.source, l.notes, l.created_at
    ].map(escape).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `crm-leads-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} lead(s)`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="crm-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">CRM / Lead Management</h1>
          <p className="text-slate-500 mt-1">Track your sales leads, companies and convert them to clients.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLeadsCSV} data-testid="crm-export-btn" title="Export current view to CSV">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={openNewCompany} data-testid="crm-new-company">
            <Building2 className="w-4 h-4 mr-2" /> New Company
          </Button>
          <Button onClick={openNewLead} data-testid="crm-new-lead">
            <Plus className="w-4 h-4 mr-2" /> New Lead
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="crm-search-input"
            placeholder="Search leads or companies by name, email, phone, company, status…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 rounded"
          />
        </div>
      </div>

      <StatsRow stats={stats} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="crm-tabs">
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">Companies</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-6">
          <PipelineBoard leads={filteredLeads} onOpen={(l) => setSelectedLead(l)} />
        </TabsContent>
        <TabsContent value="leads" className="mt-6">
          <LeadsTable leads={filteredLeads} onOpen={setSelectedLead} onEdit={openEditLead}
                      onDelete={deleteLead} onConvert={convertLead} />
        </TabsContent>
        <TabsContent value="companies" className="mt-6">
          <CompaniesTable companies={filteredCompanies} onEdit={openEditCompany} onDelete={deleteCompany} />
        </TabsContent>
      </Tabs>

      {showLeadDialog && (
        <LeadDialog
          lead={editingLead}
          companies={companies}
          onSave={saveLead}
          onClose={() => { setShowLeadDialog(false); setEditingLead(null); }}
        />
      )}
      {showCompanyDialog && (
        <CompanyDialog
          company={editingCompany}
          onSave={saveCompany}
          onClose={() => { setShowCompanyDialog(false); setEditingCompany(null); }}
        />
      )}
      {selectedLead && (
        <LeadDetailDrawer
          leadId={selectedLead.id}
          companies={companies}
          onClose={() => setSelectedLead(null)}
          onChanged={loadAll}
          onEdit={(l) => { setSelectedLead(null); openEditLead(l); }}
          onConvert={(l) => { setSelectedLead(null); convertLead(l); }}
        />
      )}
    </div>
  );
}

// ================= Sub-components =================

function StatsRow({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'from-indigo-500 to-blue-600' },
    { label: 'Pipeline Value', value: formatMoney(stats.pipeline_value), icon: TrendingUp, color: 'from-amber-500 to-orange-600' },
    { label: 'Won Value', value: formatMoney(stats.won_value), icon: Target, color: 'from-emerald-500 to-teal-600' },
    { label: 'Companies', value: stats.total_companies, icon: Building2, color: 'from-violet-500 to-purple-600' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="crm-stats">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{c.label}</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1">{c.value}</div>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function PipelineBoard({ leads, onOpen }) {
  const visible = LEAD_STATUSES.filter(s => s.value !== 'lost');
  const PAGE_SIZE = 20;
  const [limits, setLimits] = useState({});
  const bump = (v) => setLimits(l => ({ ...l, [v]: (l[v] || PAGE_SIZE) + PAGE_SIZE }));
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
      {visible.map(st => {
        const col = leads.filter(l => l.status === st.value);
        const sum = col.reduce((a, l) => a + (l.value || 0), 0);
        const limit = limits[st.value] || PAGE_SIZE;
        const shown = col.slice(0, limit);
        return (
          <div key={st.value} className="min-w-[280px] w-[280px] bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <Badge className={`${st.color} border`}>{st.label}</Badge>
              <div className="text-xs text-slate-500">
                {col.length} · {formatMoney(sum)}
              </div>
            </div>
            <div className="space-y-2">
              {shown.map(l => (
                <button
                  key={l.id}
                  onClick={() => onOpen(l)}
                  className="w-full text-left bg-white hover:shadow-md transition rounded-lg p-3 border border-slate-200"
                  data-testid={`lead-card-${l.id}`}
                >
                  <div className="font-medium text-slate-900 text-sm truncate">{l.name}</div>
                  {l.company_name && <div className="text-xs text-slate-500 truncate">{l.company_name}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs font-semibold text-slate-700">{formatMoney(l.value, l.currency)}</span>
                    <Badge variant="outline" className={`${PRIORITY_COLORS[l.priority] || ''} text-[10px]`}>
                      {l.priority || 'medium'}
                    </Badge>
                  </div>
                </button>
              ))}
              {col.length > limit && (
                <button
                  onClick={() => bump(st.value)}
                  data-testid={`pipeline-load-more-${st.value}`}
                  className="w-full text-xs font-semibold text-blue-700 hover:text-blue-900 bg-white border border-blue-200 rounded py-1.5 hover:bg-blue-50 transition-colors"
                >
                  <ChevronDown size={12} className="inline mr-1" />Show more ({col.length - limit} more)
                </button>
              )}
              {col.length === 0 && <div className="text-center text-xs text-slate-400 py-6">No leads</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadsTable({ leads, onOpen, onEdit, onDelete, onConvert }) {
  if (leads.length === 0) return <EmptyState label="No leads yet. Click 'New Lead' to add one." />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm" data-testid="leads-table">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="p-3">Name</th>
            <th className="p-3">Company</th>
            <th className="p-3">Status</th>
            <th className="p-3">Value</th>
            <th className="p-3">Source</th>
            <th className="p-3">Owner</th>
            <th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {leads.map(l => {
            const st = LEAD_STATUSES.find(s => s.value === l.status);
            return (
              <tr key={l.id} className="hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                <td className="p-3">
                  <button className="text-indigo-600 hover:underline font-medium" onClick={() => onOpen(l)}>
                    {l.name}
                  </button>
                  {l.email && <div className="text-xs text-slate-500">{l.email}</div>}
                </td>
                <td className="p-3 text-slate-700">{l.company_name || '—'}</td>
                <td className="p-3">
                  {st && <Badge className={`${st.color} border`}>{st.label}</Badge>}
                </td>
                <td className="p-3 font-medium">{formatMoney(l.value, l.currency)}</td>
                <td className="p-3 text-slate-500 text-xs">{l.source || '—'}</td>
                <td className="p-3 text-slate-500 text-xs">{l.assigned_to_name || '—'}</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    {!l.converted_to_client_id && (
                      <Button size="sm" variant="ghost" onClick={() => onConvert(l)} title="Convert to Client">
                        <UserPlus className="w-4 h-4 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onEdit(l)} title="Edit">
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(l)} title="Delete">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompaniesTable({ companies, onEdit, onDelete }) {
  if (companies.length === 0) return <EmptyState label="No companies yet. Add a company to get started." />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm" data-testid="companies-table">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="p-3">Company</th>
            <th className="p-3">Industry</th>
            <th className="p-3">Website</th>
            <th className="p-3">Contact</th>
            <th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {companies.map(c => (
            <tr key={c.id} className="hover:bg-slate-50" data-testid={`company-row-${c.id}`}>
              <td className="p-3 font-medium text-slate-900">{c.name}</td>
              <td className="p-3 text-slate-600">{c.industry || '—'}</td>
              <td className="p-3 text-indigo-600">
                {c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="hover:underline">{c.website}</a> : '—'}
              </td>
              <td className="p-3 text-slate-500 text-xs">
                {c.contact_person || '—'}<br />
                {c.contact_email || c.contact_phone}
              </td>
              <td className="p-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => onEdit(c)}><Edit className="w-4 h-4 text-slate-500" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(c)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadDialog({ lead, companies, onSave, onClose }) {
  const { currency: orgCurrency } = useCurrency();
  const [form, setForm] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    company_id: lead?.company_id || '',
    company_name: lead?.company_name || '',
    title: lead?.title || '',
    status: lead?.status || 'new',
    priority: lead?.priority || 'medium',
    value: lead?.value ?? 0,
    currency: lead?.currency || orgCurrency || 'INR',
    source: lead?.source || '',
    notes: lead?.notes || '',
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{lead?.id ? 'Edit Lead' : 'New Lead'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="lead-name" /></Field>
          <Field label="Title"><Input value={form.title} onChange={e => update('title', e.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => update('phone', e.target.value)} /></Field>
          <Field label="Company">
            <Select value={form.company_id || '__none'} onValueChange={v => {
              if (v === '__none') { update('company_id', ''); return; }
              update('company_id', v);
              const c = companies.find(x => x.id === v);
              if (c) update('company_name', c.name);
            }}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Company Name (free-text)">
            <Input value={form.company_name} onChange={e => update('company_name', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={v => update('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Deal Value">
            <Input type="number" value={form.value} onChange={e => update('value', e.target.value)} />
          </Field>
          <Field label="Currency"><Input value={form.currency} onChange={e => update('currency', e.target.value)} /></Field>
          <Field label="Source" full>
            <Input value={form.source} onChange={e => update('source', e.target.value)} placeholder="LinkedIn, referral, website…" />
          </Field>
          <Field label="Notes" full>
            <Textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.name.trim()) { toast.error('Name required'); return; }
            onSave(form);
          }} data-testid="save-lead">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyDialog({ company, onSave, onClose }) {
  const [form, setForm] = useState({
    name: company?.name || '',
    industry: company?.industry || '',
    website: company?.website || '',
    size: company?.size || '',
    address: company?.address || '',
    contact_person: company?.contact_person || '',
    contact_email: company?.contact_email || '',
    contact_phone: company?.contact_phone || '',
    notes: company?.notes || '',
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="company-dialog">
        <DialogHeader><DialogTitle>{company?.id ? 'Edit Company' : 'New Company'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="company-name" /></Field>
          <Field label="Industry"><Input value={form.industry} onChange={e => update('industry', e.target.value)} /></Field>
          <Field label="Website"><Input value={form.website} onChange={e => update('website', e.target.value)} /></Field>
          <Field label="Size"><Input value={form.size} onChange={e => update('size', e.target.value)} placeholder="10-50, 50-200…" /></Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={e => update('contact_person', e.target.value)} /></Field>
          <Field label="Contact Email"><Input value={form.contact_email} onChange={e => update('contact_email', e.target.value)} /></Field>
          <Field label="Contact Phone"><Input value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} /></Field>
          <Field label="Address"><Input value={form.address} onChange={e => update('address', e.target.value)} /></Field>
          <Field label="Notes" full><Textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.name.trim()) { toast.error('Name required'); return; }
            onSave(form);
          }} data-testid="save-company">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetailDrawer({ leadId, companies, onClose, onChanged, onEdit, onConvert }) {
  const [lead, setLead] = useState(null);
  const [activityForm, setActivityForm] = useState({ type: 'note', subject: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/crm/leads/${leadId}`, { headers: getHeaders() });
      setLead(res.data);
    } catch {
      toast.error('Failed to load lead');
      onClose();
    }
  };
  useEffect(() => { load(); }, [leadId]); // eslint-disable-line

  const addActivity = async () => {
    if (!activityForm.subject.trim()) { toast.error('Subject required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/crm/activities`, { ...activityForm, lead_id: leadId }, { headers: getHeaders() });
      setActivityForm({ type: 'note', subject: '', description: '' });
      await load();
      onChanged?.();
      toast.success('Activity added');
    } catch { toast.error('Failed to add activity'); }
    finally { setSaving(false); }
  };

  const deleteActivity = async (a) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API}/crm/activities/${a.id}`, { headers: getHeaders() });
      await load();
      onChanged?.();
    } catch { toast.error('Delete failed'); }
  };

  if (!lead) return null;
  const st = LEAD_STATUSES.find(s => s.value === lead.status);
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="lead-detail-drawer">
      <button className="flex-1 bg-slate-900/40" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Lead</div>
            <div className="text-xl font-bold text-slate-900">{lead.name}</div>
            {lead.company_name && <div className="text-sm text-slate-600">{lead.company_name}</div>}
          </div>
          <div className="flex items-center gap-2">
            {st && <Badge className={`${st.color} border`}>{st.label}</Badge>}
            {!lead.converted_to_client_id && (
              <Button size="sm" variant="outline" onClick={() => onConvert(lead)}>
                <UserPlus className="w-4 h-4 mr-1" /> Convert
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onEdit(lead)}>
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
            <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info icon={Mail} label="Email" value={lead.email || '—'} />
            <Info icon={Phone} label="Phone" value={lead.phone || '—'} />
            <Info icon={Briefcase} label="Title" value={lead.title || '—'} />
            <Info icon={DollarSign} label="Value" value={formatMoney(lead.value, lead.currency)} />
            <Info icon={Target} label="Source" value={lead.source || '—'} />
            <Info icon={Users} label="Owner" value={lead.assigned_to_name || '—'} />
          </div>
          {lead.notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
              {lead.notes}
            </div>
          )}
          {lead.converted_to_client_id && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
              ✓ Converted to client on {new Date(lead.converted_at).toLocaleDateString()}
            </div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <MessageSquare className="w-4 h-4" /> Add Activity
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Select value={activityForm.type} onValueChange={v => setActivityForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(ACTIVITY_ICONS).map(t => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Subject"
                  value={activityForm.subject}
                  onChange={e => setActivityForm(f => ({ ...f, subject: e.target.value }))}
                  data-testid="activity-subject"
                />
              </div>
              <Textarea
                placeholder="Description (optional)"
                value={activityForm.description}
                onChange={e => setActivityForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
              <Button size="sm" onClick={addActivity} disabled={saving} data-testid="add-activity">
                <Plus className="w-4 h-4 mr-1" /> Log Activity
              </Button>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Activity Timeline</div>
            {(lead.activities || []).length === 0 && (
              <div className="text-xs text-slate-400 py-4 text-center">No activities logged yet.</div>
            )}
            <div className="space-y-2">
              {(lead.activities || []).map(a => {
                const Icon = ACTIVITY_ICONS[a.type] || StickyNote;
                return (
                  <div key={a.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-900">{a.subject}</div>
                        <button onClick={() => deleteActivity(a)} className="text-slate-400 hover:text-rose-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {a.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{a.description}</div>}
                      <div className="text-[11px] text-slate-400 mt-1">
                        {a.created_by_name} · {new Date(a.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <div>
        <div className="text-[11px] text-slate-500 uppercase">{label}</div>
        <div className="text-slate-800">{value}</div>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
      {label}
    </div>
  );
}

function formatMoney(v, ccy = 'INR') {
  const num = Number(v || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(num);
  } catch {
    return `${ccy} ${num.toLocaleString()}`;
  }
}

// ================= Data Import / Export Panel =================

function DataImportExportPanel({ onImported }) {
  const [entity, setEntity] = useState('leads');
  const [format, setFormat] = useState('xlsx');
  const [downloading, setDownloading] = useState(false);
  // Export filters (leads-only)
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterSource, setFilterSource] = useState('');
  const [filterQuery, setFilterQuery] = useState('');

  // Import state
  const [importEntity, setImportEntity] = useState('leads');
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const RECRUITIZR_FIELDS_LEADS = [
    { value: '', label: '— Do not import —' },
    { value: 'name', label: 'Name (required)' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'company_name', label: 'Company' },
    { value: 'title', label: 'Job Title' },
    { value: 'status', label: 'Status (new/contacted/qualified/proposal/negotiation/won/lost)' },
    { value: 'priority', label: 'Priority (low/medium/high)' },
    { value: 'value', label: 'Deal Value' },
    { value: 'currency', label: 'Currency (INR/USD)' },
    { value: 'source', label: 'Source' },
    { value: 'assigned_to_name', label: 'Assigned To' },
    { value: 'notes', label: 'Notes' },
  ];
  const RECRUITIZR_FIELDS_COMPANIES = [
    { value: '', label: '— Do not import —' },
    { value: 'name', label: 'Name (required)' },
    { value: 'industry', label: 'Industry' },
    { value: 'website', label: 'Website' },
    { value: 'size', label: 'Size' },
    { value: 'address', label: 'Address' },
    { value: 'contact_person', label: 'Contact Person' },
    { value: 'contact_email', label: 'Contact Email' },
    { value: 'contact_phone', label: 'Contact Phone' },
    { value: 'notes', label: 'Notes' },
  ];

  const fieldOptions = importEntity === 'leads' ? RECRUITIZR_FIELDS_LEADS : RECRUITIZR_FIELDS_COMPANIES;

  const downloadExport = async () => {
    setDownloading(true);
    try {
      const params = { entity, format };
      if (entity === 'leads') {
        if (filterStatus.length) params.status = filterStatus.join(',');
        if (filterPriority.length) params.priority = filterPriority.join(',');
        if (filterSource.trim()) params.source = filterSource.trim();
      }
      if (filterQuery.trim()) params.q = filterQuery.trim();
      const res = await axios.get(`${API}/crm/export`, {
        params,
        headers: getHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `crm-${entity}-${stamp}.${format}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${entity} as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const toggleInArray = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };
  const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  const LEAD_PRIORITIES = ['low', 'medium', 'high'];

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('entity', importEntity);
      fd.append('file', f);
      const res = await axios.post(`${API}/crm/import/analyze`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setAnalysis(res.data);
      setMapping(res.data.suggested_mapping || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not analyze file');
      setAnalysis(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const commitImport = async () => {
    if (!file || !analysis) return;
    if (!Object.values(mapping).includes('name')) {
      toast.error('One column must be mapped to Name');
      return;
    }
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('entity', importEntity);
      fd.append('mapping_json', JSON.stringify(mapping));
      fd.append('file', file);
      const res = await axios.post(`${API}/crm/import/commit`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.created} row(s)`);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const resetImport = () => {
    setFile(null); setAnalysis(null); setMapping({}); setResult(null);
  };

  return (
    <div className="space-y-6" data-testid="crm-data-panel">
      {/* Export card */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900">Export CRM Data</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Download the full dataset for any entity as CSV (spreadsheet) or JSON (backup / integration).
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Entity</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger data-testid="export-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leads">Leads</SelectItem>
                  <SelectItem value="companies">Companies</SelectItem>
                  <SelectItem value="activities">Activities (timeline)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="export-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">
                    <div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel (.xlsx)</div>
                  </SelectItem>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> CSV</div>
                  </SelectItem>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2"><FileJson className="w-4 h-4" /> JSON</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={downloadExport}
                disabled={downloading}
                className="w-full"
                data-testid="export-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Preparing…' : 'Download'}
              </Button>
            </div>
          </div>
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Free-text filter (name / email / company)</Label>
              <Input
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="e.g. Acme, priya@, +91-9..."
                data-testid="export-query-input"
              />
            </div>
            {entity === 'leads' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Status (leave blank for all)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {LEAD_STATUSES.map(s => (
                      <button key={s} type="button"
                        onClick={() => toggleInArray(filterStatus, setFilterStatus, s)}
                        className={`px-2.5 py-1 text-xs rounded border ${filterStatus.includes(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}
                        data-testid={`export-status-${s}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {LEAD_PRIORITIES.map(p => (
                      <button key={p} type="button"
                        onClick={() => toggleInArray(filterPriority, setFilterPriority, p)}
                        className={`px-2.5 py-1 text-xs rounded border capitalize ${filterPriority.includes(p) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}
                        data-testid={`export-priority-${p}`}
                      >{p}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-500">Source contains (comma-separated for multiple)</Label>
                  <Input
                    value={filterSource}
                    onChange={(e) => setFilterSource(e.target.value)}
                    placeholder="e.g. LinkedIn, Referral, Website"
                    data-testid="export-source-input"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Import card */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Upload className="w-5 h-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900">Import from CSV</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Bulk-import leads or companies. Columns are auto-mapped by name — review and adjust before committing.
              </p>
            </div>
          </div>

          {!analysis && !result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Entity to import</Label>
                <Select value={importEntity} onValueChange={(v) => { setImportEntity(v); resetImport(); }}>
                  <SelectTrigger data-testid="import-entity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="leads">Leads</SelectItem>
                    <SelectItem value="companies">Companies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500">CSV file (max 10 MB)</Label>
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  disabled={analyzing}
                  data-testid="import-file-input"
                />
                {analyzing && <p className="text-xs text-slate-500 mt-1">Analyzing headers…</p>}
              </div>
            </div>
          )}

          {analysis && !result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="text-sm">
                  <span className="font-medium">{analysis.total_rows}</span> data rows detected in{' '}
                  <span className="font-medium">{file?.name}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={resetImport}>Change file</Button>
              </div>

              {analysis.missing_required?.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Missing required fields: {analysis.missing_required.join(', ')}</span>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Map CSV columns to Recruitizr fields</Label>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left p-2 w-1/3">CSV Column</th>
                        <th className="text-left p-2 w-1/3">Sample Value</th>
                        <th className="text-left p-2 w-1/3">Import as</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.headers.map((h) => (
                        <tr key={h} className="border-t">
                          <td className="p-2 font-medium">{h}</td>
                          <td className="p-2 text-slate-500 truncate max-w-[200px]">
                            {analysis.sample_rows?.[0]?.[h] || '—'}
                          </td>
                          <td className="p-2">
                            <select
                              className="w-full text-sm border rounded px-2 py-1 bg-white"
                              value={mapping[h] || ''}
                              onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}
                              data-testid={`import-map-${h}`}
                            >
                              {fieldOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={resetImport} disabled={committing}>Cancel</Button>
                <Button onClick={commitImport} disabled={committing} data-testid="import-commit-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {committing ? 'Importing…' : `Import ${analysis.total_rows} row(s)`}
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-sm">
                  <strong>{result.created}</strong> row(s) imported successfully
                  {result.skipped > 0 && <> · <strong>{result.skipped}</strong> skipped (missing name)</>}
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 max-h-40 overflow-y-auto">
                  <div className="font-semibold mb-1">{result.errors.length} error(s):</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {result.errors.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={resetImport} data-testid="import-reset-btn">Import another file</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
